import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { GlassCard, GhostButton, PrimaryButton } from "@/components/Primitives";
import Modal, { inputCls, Field } from "@/components/Modal";
import { useConfirm } from "@/components/ConfirmProvider";
import {
  Plus, Key, Copy, Eye, EyeOff, Trash2, X,
  Activity, ExternalLink, Lock, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function fmtDt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

function ScopeBadge({ scope }) {
  const isFull = scope === "full";
  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
      isFull
        ? "bg-[#007AFF]/10 text-sky-300 border-[#007AFF]/30"
        : "bg-white/[0.05] text-white/55 border-white/10"
    }`}>{isFull ? "Full access" : "Read-only"}</span>
  );
}

function StatusBadge({ k }) {
  if (k.revoked) return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-rose-400/10 text-rose-300 border-rose-400/20">Revoked</span>;
  if (k.expires_at && new Date(k.expires_at) < new Date()) {
    return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-amber-400/10 text-amber-300 border-amber-400/20">Expired</span>;
  }
  return <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-emerald-400/10 text-emerald-300 border-emerald-400/20">Active</span>;
}

function CreateDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [scope, setScope] = useState("read");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null); // {plaintext, name, scope, ...}
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(""); setScope("read"); setExpires(""); setIssued(null); setRevealed(false);
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        scope,
        expires_at: expires ? new Date(expires).toISOString() : null,
      };
      const { data } = await api.post("/api-keys", payload);
      setIssued(data);
      onCreated();
    } catch (e2) { toast.error(formatErr(e2)); }
    finally { setBusy(false); }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied"); };

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <div className="p-8" data-testid="apikey-dialog">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#007AFF]/80">Programmatic access</div>
            <div className="font-display text-2xl font-light mt-1">{issued ? "Your new API key" : "Create API key"}</div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center" data-testid="apikey-dialog-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!issued ? (
          <form onSubmit={submit} className="space-y-5">
            <Field label="Name">
              <input
                className={inputCls}
                placeholder="e.g. Make.com — Lead sync"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="apikey-name"
                autoFocus
                required
              />
            </Field>

            <Field label="Scope">
              <div className="grid grid-cols-2 gap-3">
                {[
                  { v: "read", label: "Read-only", hint: "GET endpoints only — safe for AI viewers / dashboards." },
                  { v: "full", label: "Full access", hint: "Create, update, delete — for AI agents that take action." },
                ].map((opt) => (
                  <button
                    type="button"
                    key={opt.v}
                    onClick={() => setScope(opt.v)}
                    data-testid={`apikey-scope-${opt.v}`}
                    className={`text-left p-4 rounded-2xl border transition ${
                      scope === opt.v
                        ? "border-[#007AFF]/60 bg-[#007AFF]/10 shadow-[0_0_24px_rgba(0,122,255,0.18)]"
                        : "border-white/10 bg-white/[0.02] hover:border-white/20"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {opt.v === "full" ? <ShieldCheck className="w-4 h-4 text-sky-300" /> : <Lock className="w-4 h-4 text-white/55" />}
                      <span className="font-medium">{opt.label}</span>
                    </div>
                    <div className="text-[11px] text-white/45 leading-relaxed">{opt.hint}</div>
                  </button>
                ))}
              </div>
            </Field>

            <Field label="Expires on (optional)">
              <input
                type="date"
                className={inputCls}
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
                data-testid="apikey-expires"
              />
            </Field>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-white/[0.06]">
              <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
              <PrimaryButton type="submit" disabled={busy} data-testid="apikey-submit">
                {busy ? "Creating…" : "Create key"}
              </PrimaryButton>
            </div>
          </form>
        ) : (
          <div className="space-y-5">
            <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-amber-200 text-sm leading-relaxed">
              <b>Copy this key now.</b> For your security, the plaintext is shown only once — we store only a hash.
              If you lose it, revoke this key and create a new one.
            </div>

            <Field label="API Key">
              <div className="flex items-stretch gap-2">
                <input
                  className={inputCls + " font-mono text-xs"}
                  value={revealed ? issued.plaintext : issued.plaintext.replace(/(?<=^.{12}).+/, "•".repeat(28))}
                  readOnly
                  data-testid="apikey-plaintext"
                />
                <button
                  type="button"
                  onClick={() => setRevealed((v) => !v)}
                  className="px-3 rounded-xl bg-white/[0.05] hover:bg-white/10 border border-white/10"
                  title={revealed ? "Hide" : "Reveal"}
                  data-testid="apikey-reveal"
                >
                  {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => copy(issued.plaintext)}
                  className="px-3 rounded-xl bg-[#007AFF]/15 hover:bg-[#007AFF]/25 text-sky-200 border border-[#007AFF]/30"
                  title="Copy"
                  data-testid="apikey-copy"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </Field>

            <div className="rounded-2xl bg-black/40 border border-white/10 p-4 text-xs font-mono text-white/75 overflow-x-auto">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2 font-sans">curl example</div>
              <pre className="whitespace-pre-wrap break-all">
{`curl ${BACKEND_URL}/api/clients \\
  -H "Authorization: Bearer ${issued.plaintext}"`}
              </pre>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <PrimaryButton type="button" onClick={onClose} data-testid="apikey-done">Done</PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function LogsDialog({ open, keyRec, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !keyRec) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api-keys/${keyRec.id}/logs`);
        if (!cancelled) setLogs(data);
      } catch (e) { toast.error(formatErr(e)); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, keyRec]);

  return (
    <Modal open={open} onClose={onClose} size="xl">
      <div className="p-8" data-testid="apikey-logs">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#007AFF]/80">Usage history</div>
            <div className="font-display text-2xl font-light mt-1">{keyRec?.name}</div>
            <div className="text-xs text-white/45 mt-1">{keyRec?.key_prefix}••• · {keyRec?.scope === "full" ? "Full access" : "Read-only"}</div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="rounded-2xl border border-white/[0.07] overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2.5 bg-white/[0.03] text-[10px] uppercase tracking-[0.18em] text-white/40">
            <div className="col-span-2">When</div>
            <div className="col-span-1">Method</div>
            <div className="col-span-6">Path</div>
            <div className="col-span-1 text-right">Status</div>
            <div className="col-span-2">IP</div>
          </div>
          <div className="max-h-[55vh] overflow-y-auto divide-y divide-white/[0.05]">
            {loading && <div className="p-6 text-center text-white/45 text-sm">Loading…</div>}
            {!loading && logs.length === 0 && <div className="p-6 text-center text-white/45 text-sm">No requests yet.</div>}
            {!loading && logs.map((r) => (
              <div key={r.id} className="grid grid-cols-12 px-4 py-2.5 text-xs items-center hover:bg-white/[0.02]">
                <div className="col-span-2 text-white/55">{fmtDt(r.created_at)}</div>
                <div className="col-span-1 font-mono text-white/70">{r.method}</div>
                <div className="col-span-6 font-mono text-white/65 truncate" title={r.path}>{r.path}</div>
                <div className={`col-span-1 text-right font-mono ${r.status_code >= 400 ? "text-rose-300" : "text-emerald-300"}`}>{r.status_code}</div>
                <div className="col-span-2 text-white/45 truncate">{r.ip || "—"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default function ApiKeysSection() {
  const [keys, setKeys] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [logKey, setLogKey] = useState(null);
  const confirm = useConfirm();

  const load = async () => {
    try {
      const { data } = await api.get("/api-keys");
      setKeys(data);
    } catch (e) { toast.error(formatErr(e)); }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (k) => {
    const ok = await confirm({
      title: `Revoke "${k.name}"?`,
      description: "Any integration using this key will stop working immediately. This cannot be undone.",
      confirmLabel: "Revoke key",
    });
    if (!ok) return;
    try { await api.delete(`/api-keys/${k.id}`); toast.success("Key revoked"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <GlassCard className="lg:col-span-2" testId="settings-api-keys">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-white/[0.05] flex items-center justify-center">
            <Key className="w-4 h-4 text-[#007AFF]" />
          </div>
          <div>
            <div className="font-display text-xl">API Keys</div>
            <div className="text-xs text-white/45">Let AI agents, Make.com, Zapier, n8n, custom GPTs read & update your CRM.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${BACKEND_URL}/api/docs`}
            target="_blank" rel="noopener noreferrer"
            className="text-xs text-white/55 hover:text-sky-300 inline-flex items-center gap-1.5"
            data-testid="apikey-docs-link"
          >
            <ExternalLink className="w-3.5 h-3.5" /> OpenAPI / Swagger
          </a>
          <PrimaryButton onClick={() => setOpenCreate(true)} data-testid="new-apikey-btn">
            <Plus className="w-4 h-4" /> New key
          </PrimaryButton>
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.07] overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2.5 bg-white/[0.03] text-[10px] uppercase tracking-[0.18em] text-white/40">
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Prefix</div>
          <div className="col-span-2">Scope</div>
          <div className="col-span-2">Last used</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        <div className="divide-y divide-white/[0.05]">
          {keys.length === 0 && (
            <div className="p-10 text-center text-white/45 text-sm">
              No API keys yet. Create one above to give an integration access.
            </div>
          )}
          {keys.map((k) => (
            <div key={k.id} className="grid grid-cols-12 px-4 py-3 items-center text-sm hover:bg-white/[0.02]" data-testid={`apikey-row-${k.id}`}>
              <div className="col-span-3">
                <div className="text-white font-medium truncate">{k.name}</div>
                <div className="text-[11px] text-white/40 mt-0.5">
                  {k.request_count || 0} requests · created {fmtDt(k.created_at)}
                </div>
              </div>
              <div className="col-span-2 font-mono text-xs text-white/55">{k.key_prefix}•••</div>
              <div className="col-span-2"><ScopeBadge scope={k.scope} /></div>
              <div className="col-span-2 text-white/55 text-xs">{fmtDt(k.last_used_at)}</div>
              <div className="col-span-2"><StatusBadge k={k} /></div>
              <div className="col-span-1 flex items-center justify-end gap-1">
                <button
                  type="button"
                  onClick={() => setLogKey(k)}
                  className="text-white/55 hover:text-white p-1.5"
                  title="Usage logs"
                  data-testid={`apikey-logs-${k.id}`}
                >
                  <Activity className="w-3.5 h-3.5" />
                </button>
                {!k.revoked && (
                  <button
                    type="button"
                    onClick={() => revoke(k)}
                    className="text-rose-400/80 hover:text-rose-300 p-1.5"
                    title="Revoke"
                    data-testid={`apikey-revoke-${k.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 rounded-2xl bg-black/30 border border-white/[0.06] p-4 text-xs text-white/55">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">How to use</div>
        <div className="space-y-1">
          <div>Send the key as a header on any request:</div>
          <div className="font-mono text-white/70">Authorization: Bearer eqk_live_…</div>
          <div className="text-white/45">or</div>
          <div className="font-mono text-white/70">X-API-Key: eqk_live_…</div>
          <div className="mt-2">Full schema lives at <a href={`${BACKEND_URL}/api/docs`} target="_blank" rel="noopener noreferrer" className="text-sky-300 hover:underline">/api/docs</a> (OpenAPI 3) — paste this into a ChatGPT custom GPT action, Claude MCP, n8n HTTP node, Make.com, or Zapier.</div>
        </div>
      </div>

      <CreateDialog open={openCreate} onClose={() => setOpenCreate(false)} onCreated={load} />
      <LogsDialog open={!!logKey} keyRec={logKey} onClose={() => setLogKey(null)} />
    </GlassCard>
  );
}
